/**
 * Sticky one-way downgrade contract — PRODUCT DECISION D1, as amended.
 *
 * Once a peer is degraded *for cause* (sustained congestion, a latency-budget breach, or a
 * decode-lag episode), it does not auto-climb back on a hunch. This module is the pure,
 * shared definition of that ratchet: a monotone per-session floor that only rises through
 * `applySessionDowngrade`. The amendment: the floor may be RELEASED, one rung at a time,
 * through `applySessionRelease` — and only by a caller holding measured evidence that the
 * cause is gone (see connectSettleContracts `canReclaim`). Raising stays cheap and
 * automatic; lowering is deliberate and evidenced. It replaces the climb-back philosophy the
 * recovery manager and latency climb gate were built on.
 *
 * "For cause" means a SUSTAINED bad condition, not a transient blip — the dwell/hold windows
 * (congestion dwell, latency hold, decode-lag episode) decide what counts, and this module
 * only records the resulting floor. A blip that recovers inside its window never reaches
 * this ratchet.
 *
 * Browser-free and Node/Jest-safe: types and pure functions only.
 */

/** One-way session floor. Order matters: index = severity (higher = more degraded). */
export const SESSION_DOWNGRADE_LADDER = ['none', 'soft', 'hard', 'tier360'] as const;
export type SessionDowngradeFloor = (typeof SESSION_DOWNGRADE_LADDER)[number];

export type SessionDowngradeState = {
  /** Rises through applySessionDowngrade; lowers only through applySessionRelease. */
  floor: SessionDowngradeFloor;
  appliedAtMs?: number;
  reason?: string;
  /** Set by the most recent release; cleared when the floor next rises. */
  releasedAtMs?: number;
  releaseReason?: string;
};

function severity(floor: SessionDowngradeFloor): number {
  return SESSION_DOWNGRADE_LADDER.indexOf(floor);
}

/** True when `a` is at least as severe as `b`. */
export function floorAtLeast(a: SessionDowngradeFloor, b: SessionDowngradeFloor): boolean {
  return severity(a) >= severity(b);
}

/**
 * Pure monotone ratchet: the returned floor is the more severe of `current.floor` and
 * `requested`. `appliedAtMs`/`reason` update only when the floor actually rises; a request
 * at or below the current floor returns `current` unchanged (same object identity), so a
 * no-op is detectable and memo-friendly.
 */
export function applySessionDowngrade(
  current: SessionDowngradeState,
  requested: SessionDowngradeFloor,
  nowMs: number,
  reason: string,
): SessionDowngradeState {
  if (severity(requested) <= severity(current.floor)) {
    return current;
  }
  return { floor: requested, appliedAtMs: nowMs, reason };
}

/**
 * Pure evidenced release: lower the floor to `to`. A request at or above the current floor
 * returns `current` unchanged (same object identity) — this function can never raise. The
 * raise metadata (`appliedAtMs`/`reason`) is preserved so a record shows both why the floor
 * went up and why it came down.
 */
export function applySessionRelease(
  current: SessionDowngradeState,
  to: SessionDowngradeFloor,
  nowMs: number,
  reason: string,
): SessionDowngradeState {
  if (severity(to) >= severity(current.floor)) {
    return current;
  }
  return {
    ...current,
    floor: to,
    releasedAtMs: nowMs,
    releaseReason: reason,
  };
}

/** The initial, never-degraded session state. */
export function initialSessionDowngradeState(): SessionDowngradeState {
  return { floor: 'none' };
}
